// Cliente HTTP base hacia el backend Salonika.
// Usa Bearer token guardado en localStorage. La URL la toma de VITE_API_URL.
//
// - En dev (apps/web/.env): VITE_API_URL=http://localhost:4000 → llama a la API en su puerto.
// - En prod (Docker): VITE_API_URL="" (vacío) → BASE queda vacío y los fetch usan rutas
//   relativas (`/api/...`). Nginx en el container web hace proxy_pass a la API.
// - Si la variable está undefined (caso raro), usamos el fallback de localhost para dev.
//
// Mensajes que arma el propio cliente (no los del backend): se traducen con
// textoDe, que lee el idioma actual en el momento de la llamada.
import { textoDe } from '../i18n/textos';
import { textosComun } from '../i18n/modulos/comun';
import { conReintentos, esFallaDeRed, nuevaLlave, GUARDAR_TIMEOUT_MS } from '../lib/conexion';

// IMPORTANTE: usamos `??` y no `||` porque "" es falsy pero queremos preservarlo.
const BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000') as string;
const TOKEN_KEY = 'salonika_token';
/** Último usuario confirmado por el servidor (ver api/auth.ts). Vive y muere con el token. */
export const USUARIO_KEY = 'salonika_usuario';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string | null) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else {
      localStorage.removeItem(TOKEN_KEY);
      // Sin sesión no se recuerda a nadie: el próximo arranque sin red no
      // puede revivir a un usuario que salió o al que se le venció la sesión.
      localStorage.removeItem(USUARIO_KEY);
    }
  },
};

// Endpoints donde un 401 significa "credenciales malas", no "sesión vencida".
const ES_LOGIN = /^\/api\/auth\/(login|refresh)\b/;

// =============================================================================
// Sesión vencida — aviso a toda la app.
//
// POR QUÉ EXISTE: en ui-alert-context había un comentario que decía "401 ya
// tiene su propio flujo de redirect a login". Ese flujo NO existía. El cliente
// borraba el token y lanzaba el error, y ahí moría: cada `.catch()` de cada
// pantalla se lo tragaba en silencio y la app seguía dibujando datos viejos
// como si nada. El usuario se enteraba recién al hacer clic en algo y recibir
// "Token faltante".
//
// Ahora el aviso sube hasta UserProvider, que limpia el usuario y devuelve la
// pantalla de login. Un solo suscriptor alcanza; el Set es para no pelearse
// con el StrictMode de React, que monta y desmonta dos veces en dev.
// =============================================================================
type SesionExpiradaHandler = () => void;
const subsSesionExpirada = new Set<SesionExpiradaHandler>();
let avisando = false;

export function onSesionExpirada(fn: SesionExpiradaHandler): () => void {
  subsSesionExpirada.add(fn);
  return () => { subsSesionExpirada.delete(fn); };
}

function emitSesionExpirada() {
  // Cuando la sesión se vence, varias llamadas en vuelo fallan a la vez. Sin
  // este candado el usuario recibe cinco avisos idénticos de una.
  if (avisando) return;
  avisando = true;
  // Se libera en el siguiente tick: es un aviso por ráfaga, no uno por vida.
  setTimeout(() => { avisando = false; }, 0);
  for (const fn of subsSesionExpirada) {
    try { fn(); } catch (err) { console.error('[api] fallo avisando sesión expirada:', err); }
  }
}

export class ApiError extends Error {
  /**
   * `body` es el JSON de error tal cual lo mandó el backend. Sin esto, un 409
   * con datos útiles —por ejemplo el item de inventario que ya existe— llegaba
   * al front convertido en un string y quien lo atrapaba no podía reaccionar,
   * solo mostrar el mensaje.
   */
  constructor(public status: number, message: string, public body?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

// =============================================================================
// Event bus de llamadas API — el LoggingProvider se subscribe para registrar
// cada request/response en el sistema de logs interno.
// =============================================================================
export interface ApiCallEvent {
  method: string;          // GET / POST / PUT / PATCH / DELETE
  path: string;            // ej. /api/menu
  status: number;          // 0 = network error
  ok: boolean;
  durationMs: number;
  requestBody?: unknown;   // ya parseado a JSON cuando aplica
  responseBody?: unknown;  // ya parseado a JSON cuando aplica
  errorMessage?: string;
  /**
   * true SOLO cuando había una sesión y el backend la rechazó. Un 401 sin token
   * enviado no es una sesión que se venció: es alguien que todavía no entró, y
   * decirle "tu sesión expiró" en su primera visita es mentirle.
   */
  sesionExpirada?: boolean;
}

type ApiListener = (e: ApiCallEvent) => void;
const apiListeners = new Set<ApiListener>();

export function onApiCall(fn: ApiListener): () => void {
  apiListeners.add(fn);
  return () => { apiListeners.delete(fn); };
}

function emitApiCall(e: ApiCallEvent) {
  // Síncrono — el listener corre ANTES de que se siga ejecutando el resto
  // del request (típicamente un throw). Tener el toast visible antes de que
  // el caller maneje el error garantiza que la UI siempre informa de fallos
  // 5xx / network incluso cuando el caller silencia el ApiError.
  for (const l of apiListeners) {
    try { l(e); } catch { /* listener no debe romper el HTTP */ }
  }
}

// =============================================================================
// Cambios con llave (modo contingencia, paso 2 · oct-2026). El monitor de
// conexión escucha esto para pintar "Guardando N cambios…" y para saber la
// hora del último guardado confirmado. Ver app/lib/monitor-conexion.ts.
// =============================================================================
export interface EventoGuardado {
  id: number;
  /** inicio → (reintento)* → ok | error */
  fase: 'inicio' | 'reintento' | 'ok' | 'error';
  t: number;
}
type GuardadoListener = (e: EventoGuardado) => void;
const guardadoListeners = new Set<GuardadoListener>();
let siguienteGuardado = 1;

export function onGuardado(fn: GuardadoListener): () => void {
  guardadoListeners.add(fn);
  return () => { guardadoListeners.delete(fn); };
}

function emitGuardado(e: EventoGuardado) {
  for (const l of guardadoListeners) {
    try { l(e); } catch { /* un oyente no debe romper el guardado */ }
  }
}

interface OpcionesRequest {
  /** Corta la espera a los N ms. El resultado es "no sabemos" (status 0), no "no se hizo". */
  timeoutMs?: number;
  /** Intento intermedio de un reintento: una falla de red no se avisa (ni toast ni log). */
  callarFallaDeRed?: boolean;
}

async function request<T>(path: string, init: RequestInit = {}, opts: OpcionesRequest = {}): Promise<T> {
  // Con FormData NO se pone Content-Type: el navegador lo arma solo con el
  // boundary del multipart. Si lo forzamos a JSON, multer no encuentra el archivo.
  const esForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(esForm ? {} : { 'Content-Type': 'application/json' }),
    ...((init.headers as Record<string, string>) || {}),
  };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  const method = (init.method || 'GET').toUpperCase();
  const t0 = performance.now();
  // Un multipart no se registra en los logs: serían megas de binario.
  const requestBody = esForm ? '[multipart]' : init.body ? safeParse(String(init.body)) : undefined;

  // Sin timeoutMs el fetch espera lo que haga falta, como siempre.
  const ctrl = opts.timeoutMs ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), opts.timeoutMs) : null;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, ...(ctrl ? { signal: ctrl.signal } : {}) });
  } catch (err: any) {
    const porTiempo = !!ctrl?.signal.aborted;
    const motivo = porTiempo
      ? textoDe(textosComun, 'noRespondioATiempo', { s: Math.round((opts.timeoutMs || 0) / 1000) })
      : (err?.message || 'Network error');
    const ev: ApiCallEvent = {
      method, path, status: 0, ok: false,
      durationMs: performance.now() - t0,
      requestBody,
      errorMessage: motivo,
    };
    if (!opts.callarFallaDeRed) emitApiCall(ev);
    throw new ApiError(0, textoDe(textosComun, 'noSeConectoBackend', { base: BASE, error: motivo }));
  } finally {
    if (timer) clearTimeout(timer);
  }

  const text = await res.text();
  const body = text ? safeJson(text) : null;
  const durationMs = performance.now() - t0;

  if (res.status === 401) {
    // Lo que distingue los dos casos NO es si mandamos token, es QUÉ endpoint
    // se llamó.
    //
    // El bug de ago-2026: se usaba `!token` para decidir. Después del primer
    // 401 el token se borraba de localStorage, así que TODAS las llamadas
    // siguientes salían sin token y el backend contestaba "Token faltante" —
    // que es lo que terminaba viendo el usuario. Un mensaje que no significa
    // nada para quien solo quiere seguir trabajando, y que además tapaba el
    // hecho real: la sesión se venció (TTL de 12 h) y hay que volver a entrar.
    const esLogin = ES_LOGIN.test(path);

    if (esLogin) {
      const errMsg = body?.error || textoDe(textosComun, 'credencialesInvalidas');
      emitApiCall({
        method, path, status: 401, ok: false, durationMs,
        requestBody, responseBody: body, errorMessage: errMsg,
      });
      throw new ApiError(401, errMsg);
    }

    // Un 401 en cualquier otro endpoint significa una sola cosa accionable:
    // la sesión ya no sirve. Da igual si el backend dijo "Token faltante" o
    // "Sesión inválida o expirada".
    //
    // `token` acá NO decide el mensaje (ese fue el bug), solo decide si vale la
    // pena avisar: si no mandamos token, no había sesión que vencer.
    const habiaSesion = !!token;
    tokenStore.set(null);
    const errMsg = habiaSesion
      ? textoDe(textosComun, 'sesionExpiroVolve')
      : textoDe(textosComun, 'necesitasIniciarSesion');
    emitApiCall({
      method, path, status: 401, ok: false, durationMs,
      requestBody, responseBody: body,
      sesionExpirada: habiaSesion,
      // El mensaje crudo del backend se conserva para depurar, no para mostrar.
      errorMessage: `${errMsg} (backend: ${body?.error || textoDe(textosComun, 'sinDetalle')})`,
    });
    if (habiaSesion) emitSesionExpirada();
    throw new ApiError(401, errMsg, body);
  }

  if (!res.ok) {
    const errMsg = body?.error || res.statusText || textoDe(textosComun, 'errorDesconocido');
    if (!(opts.callarFallaDeRed && esFallaDeRed(res.status))) {
      emitApiCall({
        method, path, status: res.status, ok: false, durationMs,
        requestBody, responseBody: body, errorMessage: errMsg,
      });
    }
    throw new ApiError(res.status, errMsg, body);
  }

  emitApiCall({
    method, path, status: res.status, ok: true, durationMs,
    requestBody, responseBody: body,
  });
  return body as T;
}

function safeJson(text: string): any {
  try { return JSON.parse(text); } catch { return null; }
}

function safeParse(text: string): any {
  try { return JSON.parse(text); } catch { return text; }
}

/**
 * POST con llave contra duplicados (modo contingencia, paso 2 · oct-2026).
 *
 * Una llave por cada cambio (no por intento). Si la red falla o el servidor
 * tarda más de GUARDAR_TIMEOUT_MS, se reintenta con la MISMA llave: si el
 * primer intento sí quedó, el servidor devuelve esa respuesta en vez de crear
 * otro pedido u otra línea. Un 400 o un 422 son respuestas reales y no se
 * reintentan.
 *
 * Si se agotan los intentos sin respuesta, el error dice que NO sabemos si
 * quedó (body.incierto = true). No es lo mismo que "no se guardó".
 */
async function postIdempotente<T>(path: string, body: unknown): Promise<T> {
  return postConLlave<T>(path, body, nuevaLlave());
}

/**
 * Lo mismo con una llave que ya existe y headers extra. La usa la subida de la
 * caja de contingencia (paso 3d): la llave se creó cuando el cajero hizo el
 * cambio, sin internet, y va con su hora original (X-Hecho-En).
 */
async function postConLlave<T>(path: string, body: unknown, llave: string, extra: Record<string, string> = {}): Promise<T> {
  const id = siguienteGuardado++;
  const cuerpo = JSON.stringify(body);
  emitGuardado({ id, fase: 'inicio', t: Date.now() });
  try {
    const r = await conReintentos<T>(
      (n, ultimo) => {
        if (n > 1) emitGuardado({ id, fase: 'reintento', t: Date.now() });
        return request<T>(
          path,
          { method: 'POST', body: cuerpo, headers: { ...extra, 'Idempotency-Key': llave } },
          { timeoutMs: GUARDAR_TIMEOUT_MS, callarFallaDeRed: !ultimo },
        );
      },
      { reintentable: (e) => e instanceof ApiError && esFallaDeRed(e.status) },
    );
    emitGuardado({ id, fase: 'ok', t: Date.now() });
    return r;
  } catch (e) {
    emitGuardado({ id, fase: 'error', t: Date.now() });
    if (e instanceof ApiError && esFallaDeRed(e.status)) {
      throw new ApiError(e.status, textoDe(textosComun, 'noSeConfirmoGuardado'), { ...(e.body ?? {}), incierto: true });
    }
    throw e;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  /** POST con llave y reintentos seguros. Solo para endpoints que aceptan Idempotency-Key. */
  postIdempotente: <T>(path: string, body: unknown) => postIdempotente<T>(path, body),
  /** Subida de la caja de contingencia: la llave y la hora original del cambio hecho sin internet. */
  postSubida: <T>(path: string, body: unknown, a: { llave: string; hechoEn: string }) =>
    postConLlave<T>(path, body, a.llave, { 'X-Hecho-En': a.hechoEn }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** POST multipart (subida de archivos). */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
};

export const apiBaseUrl = BASE;
